import { useUser } from '@clerk/clerk-react'
import { useStore } from '../store/useStore'
import { User, Users } from 'lucide-react'

export default function ModeToggle({ onGroupClick }) {
  const { isSignedIn, isLoaded } = useUser()
  const { collaborationMode, currentRoomId, currentRoomName, collaborators, leaveRoom } = useStore()
  
  // Gracefully handle Clerk not loaded (timeout on satellite domain)
  const clerkReady = isLoaded && isSignedIn
  
  const isSolo = collaborationMode === 'solo'
  
  const handleSoloClick = async () => {
    if (!isSolo && currentRoomId) {
      // Notify D1 API to decrement user count
      try {
        await fetch('/api/rooms', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId: currentRoomId, action: 'leave' })
        })
      } catch (e) {
        console.error('[ModeToggle] Failed to notify room leave:', e)
      }
      leaveRoom()
    }
  }
  
  const handleGroupClick = () => {
    if (!clerkReady) {
      // Clerk not loaded or not signed in - can't use group mode
      return
    }
    onGroupClick?.()
  }
  
  return (
    <div className="flex items-center gap-1 bg-ide-bg rounded-lg p-0.5 border border-ide-border">
      {/* Solo Mode Button */}
      <button
        onClick={handleSoloClick}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          isSolo 
            ? 'bg-ide-surface text-ide-text shadow-sm' 
            : 'text-ide-textMuted hover:text-ide-text'
        }`}
        title="Work alone"
      >
        <User size={14} />
        <span>Solo</span>
      </button>
      
      {/* Group Mode Button */}
      <button
        onClick={handleGroupClick}
        disabled={!clerkReady}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
          !isSolo 
            ? 'bg-ide-accent/20 text-ide-accent shadow-sm' 
            : clerkReady 
              ? 'text-ide-textMuted hover:text-ide-text' 
              : 'text-ide-textDim cursor-not-allowed'
        }`}
        title={clerkReady ? 'Join a social coding room' : 'Sign in to use social coding'}
      >
        <Users size={14} />
        <span>Group</span>
        {!isSolo && collaborators.length > 0 && (
          <span className="bg-ide-accent text-white text-[10px] px-1.5 rounded-full">
            {collaborators.length}
          </span>
        )}
      </button>
      
      {/* Room indicator when in group mode */}
      {!isSolo && currentRoomName && (
        <div className="ml-1 px-2 py-1 bg-ide-accent/10 rounded text-xs text-ide-accent truncate max-w-[120px]" title={currentRoomName}>
          {currentRoomName}
        </div>
      )}
    </div>
  )
}
